import {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useAuth, useAutoSignin} from 'react-oidc-context';
import {fetchCurrentUser} from '@/services/global-services/currentUserService.js';
import store from '@/store/store.js';
import {SESSION_STATUS, setCurrentUser, setRoles, setCurrentUserError, setCurrentUserLoading, setSessionRefreshUnavailable} from '@/store/slices/authSlice.js';
import {jwtDecode} from 'jwt-decode';
import {getValidAccessToken, isSessionActive, isTransientRefreshError, logout, SESSION_LOGOUT_REASONS} from '@/services/auth/sessionService.js';
import {Label} from '@/components/atoms/texts/label';
import {Button} from '@/components/ui/button';
import logoWithBrand from '/src/assets/images/logoWithBrand.png';
import {useAppSelector} from '@/store/hooks.js';

export const ProtectedApp = ({children}) => {
    const sessionStatus = useAppSelector((state) => state.auth.sessionStatus);
    const auth = useAuth();

    if (sessionStatus !== SESSION_STATUS.ACTIVE) {
        return <div className="loading-container">
            <img src={logoWithBrand} alt="Kubrik 21" style={{height: '350px', width: 'auto', objectFit: 'contain'}}/>
            <div className="loading-content flex flex-col items-center gap-4" role="status">
                <Label message={sessionStatus === SESSION_STATUS.LOGGING_OUT ? 'connection.signingOut' : 'connection.sessionEnded'}/>
                {sessionStatus === SESSION_STATUS.ENDED && <Button
                    text="connection.reconnect"
                    disabled={auth.activeNavigator === 'signinRedirect'}
                    onClick={() => { void auth.signinRedirect(); }}
                />}
                {auth.error?.source === 'signinRedirect' && <Label message="connection.reconnectFailed" color="error"/>}
            </div>
        </div>;
    }

    return <AuthenticatedApp>{children}</AuthenticatedApp>;
};

const AuthenticatedApp = ({children}) => {
    const {
        isLoading: isKeycloakLoading,
        isAuthenticated,
        error: keycloakError
    } = useAutoSignin({signinMethod: 'signinRedirect'});

    const currentUserError = useAppSelector((state) => state.auth.error);
    const currentUser = useAppSelector((state) => state.auth.currentUser);
    const isCurrentUserLoading = useAppSelector((state) => state.auth.loading);
    const refreshUnavailable = useAppSelector((state) => state.auth.refreshUnavailable);
    const temporaryRenewError = keycloakError &&
        ['renewSilent', 'signinSilent'].includes(keycloakError.source) && isTransientRefreshError(keycloakError);

    useEffect(() => {
        if (!isSessionActive()) return;
        if (keycloakError && !temporaryRenewError) {
            void logout({reason: SESSION_LOGOUT_REASONS.OIDC_ERROR});
            return;
        }
        if (temporaryRenewError) store.dispatch(setSessionRefreshUnavailable(true));
        if (isKeycloakLoading || !isAuthenticated || currentUser || currentUserError || refreshUnavailable || temporaryRenewError) return;

        let cancelled = false;
        const loadUser = async () => {
            let tokenData;
            try {
                const token = await getValidAccessToken();
                if (cancelled || !isSessionActive()) return;
                tokenData = jwtDecode(token);
            } catch (error) {
                if (!cancelled && isSessionActive()) {
                    if (isTransientRefreshError(error)) {
                        store.dispatch(setSessionRefreshUnavailable(true));
                    } else {
                        void logout({reason: SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE});
                    }
                }
                return;
            }

            try {
                const currentUserData = await fetchCurrentUser();
                if (cancelled || !isSessionActive()) return;
                if (currentUserData.error || !currentUserData.data) {
                    if (isTransientRefreshError(currentUserData.error)) {
                        store.dispatch(setSessionRefreshUnavailable(true));
                    } else {
                        store.dispatch(setCurrentUserError(String(currentUserData.error ?? 'unknown_error')));
                    }
                } else {
                    store.dispatch(setCurrentUser(currentUserData.data));
                    store.dispatch(setRoles(tokenData.realm_access?.roles || []));
                }
            } catch (error) {
                if (!cancelled && isSessionActive()) {
                    if (isTransientRefreshError(error)) {
                        store.dispatch(setSessionRefreshUnavailable(true));
                    } else {
                        store.dispatch(setCurrentUserError(String(error)));
                    }
                }
            } finally {
                if (!cancelled && isSessionActive()) {
                    store.dispatch(setCurrentUserLoading(false));
                }
            }
        };
        void loadUser();
        return () => { cancelled = true; };
    }, [isKeycloakLoading, keycloakError, isAuthenticated, currentUser, currentUserError, refreshUnavailable, temporaryRenewError]);

    const recovery = (refreshUnavailable || temporaryRenewError) && <SessionRecovery/>;
    if (isKeycloakLoading || !isAuthenticated || (keycloakError && !temporaryRenewError) || isCurrentUserLoading || currentUserError || !currentUser) {
        return <div className="loading-container">
            <img src={logoWithBrand} alt="Kubrik 21" style={{height: '350px', width: 'auto', objectFit: 'contain'}}/>
            <div className="loading-content">
                <Label message="connection.welcome" className="text"/>
                <Label message="connection.appName" className="loading-title text" size="h1"/>
                <div className="text">
                    {isKeycloakLoading && <Label message="connection.loadingIdentity"/>}
                    {!isKeycloakLoading && isAuthenticated && isCurrentUserLoading && <Label message="connection.loadingUser"/>}
                    {keycloakError && <Label message="connection.identityError"/>}
                    {currentUserError && <Label message="connection.accessDenied"/>}
                    {!isKeycloakLoading && !isAuthenticated && !keycloakError && <Label message="connection.loginRequired"/>}
                </div>
            </div>
            {recovery}
        </div>;
    }

    return <>{children}{recovery}</>;
};

const SessionRecovery = () => {
    const [retrying, setRetrying] = useState(false);
    const retry = async () => {
        setRetrying(true);
        try {
            await getValidAccessToken(true);
        } catch {
            // Le service conserve l'état temporaire ou termine la session en cas de rejet définitif.
        } finally {
            setRetrying(false);
        }
    };

    return <aside role="status" className="fixed bottom-4 left-4 z-40 max-w-lg rounded-lg border bg-white p-4 shadow-lg">
        <Label message="connection.refreshUnavailable" className="mb-3 block"/>
        <Button text="connection.retrySession" disabled={retrying} onClick={retry}/>
    </aside>;
};

ProtectedApp.propTypes = {children: PropTypes.node};
AuthenticatedApp.propTypes = {children: PropTypes.node};
